import { T } from '@salecycle/experimental/Typography/Typography';
import { TargetAudience } from './components/TargetAudience';
import {
  useAddNewTargetAudience,
  useDeleteTargetAudience,
  useSetTargetAudience,
} from '../../state/actions/useSetTargetAudience';
import { useCampaignEditorContext } from '../../state/hooks/useCampaignEditorContext';
import { useTranslation } from 'react-i18next';
import { AdditionalSegmentation } from './components/AdditionalSegmentation';
import { PurchaseCheck } from './components/PurchaseCheck';
import { Divider } from '../Divider';
import { PurchaseCheckUnit, TargetAudienceType, TargetLookbackPeriod } from '../../state/CampaignEditorState';
import { Button, SvgIcon, SvgIconButton, useTheme } from '@salecycle/ui-component-library';
import { BulkSortByFeature } from './components/BulkSortByfeature';
import { ButtonContainer } from 'sections/design/template-management/sections/template-list/components/styles';
import { useFeatureFlags } from 'core/app/AppContext';
import { Moment } from 'moment-timezone';
import { useSetPurchaseCheckAll } from '../../state/actions/useSetPurchaseCheckAll';

const AddAudienceButton = () => {
  const { state } = useCampaignEditorContext();
  const { flagEnabled } = useFeatureFlags();
  const onAudienceAdd = useAddNewTargetAudience();

  if (!flagEnabled('ld-bulk-targeting')) {
    return null;
  }

  return (
    <div>
      <Button
        aria-label={'Add Audience'}
        variation="secondary"
        leftSideIcon={<SvgIcon name="plus" />}
        disabled={state.campaign.targetAudience.length >= 3 || state.readOnly}
        onClick={onAudienceAdd}
      >
        Add Audience
      </Button>
    </div>
  );
};

export const AudienceTab = () => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();
  const { state } = useCampaignEditorContext();
  const { flagEnabled } = useFeatureFlags();
  const featureBulkDatePickers = flagEnabled('ld-bulk-date-pickers');

  const onAudienceChanged = useSetTargetAudience();
  const deleteAudienceButton = useDeleteTargetAudience();
  const setPurchaseCheckAll = useSetPurchaseCheckAll();

  return (
    <>
      <T variant="headingH1">{t('bulkRetargetingCampaigns.editor.tabs.audience')}</T>
      <div style={{ display: 'flex', gap: theme.designToken.spacing3, flexDirection: 'column' }}>
        <T variant="headingH2">{t('bulkRetargetingCampaigns.editor.targetAudience')}</T>
        <T>{t('bulkRetargetingCampaigns.editor.targetAudienceDescription')}</T>
      </div>
      <BulkSortByFeature />
      {state.campaign.targetAudience.map((targetAudience, index) => (
        <>
          <div style={{ display: 'flex', gap: theme.designToken.spacing3, flexDirection: 'row' }}>
            <TargetAudience
              key={index}
              onChange={(audience: {
                audience: TargetAudienceType;
                days: number;
                dateType?: TargetLookbackPeriod;
                startDate?: Moment;
                endDate?: Moment;
                isUsingDateRange?: boolean;
              }) => {
                onAudienceChanged(audience, index);
              }}
              value={targetAudience}
              disabled={state?.readOnly}
            />
            {state.campaign.targetAudience.length > 1 && (
              <ButtonContainer>
                <SvgIconButton
                  ariaLabel={t(`bulkRetargetingCampaigns.editor.delete`)}
                  name="trash"
                  size="32"
                  onClick={() => deleteAudienceButton(targetAudience, index)}
                />
              </ButtonContainer>
            )}
          </div>
        </>
      ))}
      <AddAudienceButton />
      <Divider />
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing3 }}>
        <T variant="headingH2">{t('bulkRetargetingCampaigns.editor.additionalSegmentation')}</T>
        <T>{t('bulkRetargetingCampaigns.editor.additionalSegmentationDescription')}</T>
      </div>

      <div style={{ display: 'grid', gap: theme.designToken.spacing3, gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {state.campaign.targetAudience.map((targetAudience, index) => (
          <AdditionalSegmentation key={index} targetAudience={targetAudience} />
        ))}
      </div>
      <Divider />
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing3 }}>
        <T variant="headingH2">{t('bulkRetargetingCampaigns.editor.purchaseCheck')}</T>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing3 }}>
        {state.campaign.targetAudience.map(targetAudience => {
          // Always read enablePurchaseCheck from direct state as source of truth
          const enablePurchaseCheck = state.campaign.enablePurchaseCheck[targetAudience.audience] ?? false;

          const purchaseCheckValue = featureBulkDatePickers
            ? // When feature flag is enabled: use purchaseCheck object
              (() => {
                const purchaseCheckData = state.campaign.purchaseCheck?.[targetAudience.audience];
                if (purchaseCheckData) {
                  // Use purchaseCheck object if it exists, but override enablePurchaseCheck with direct state
                  return {
                    targetAudience,
                    ...purchaseCheckData,
                    enablePurchaseCheck, // Always use the most up-to-date value from direct state
                  };
                } else {
                  // Fallback: create from direct state values (for backward compatibility)
                  // purchaseCheckData is undefined here, so use defaults
                  return {
                    targetAudience,
                    enablePurchaseCheck,
                    purchaseCheckDays: state.campaign.purchaseCheckDays[targetAudience.audience] ?? 1,
                    purchaseCheckMonths: 1,
                    purchaseCheckUnit: PurchaseCheckUnit.Days,
                    purchaseCheckStartDate: null,
                    purchaseCheckEndDate: null,
                    isPurchaseCheckUsingDateRange: false,
                  };
                }
              })()
            : // When feature flag is disabled: use enablePurchaseCheck and purchaseCheckDays
              {
                targetAudience,
                enablePurchaseCheck,
                purchaseCheckDays: state.campaign.purchaseCheckDays[targetAudience.audience] ?? 1,
                purchaseCheckMonths: 1,
                purchaseCheckUnit: PurchaseCheckUnit.Days,
                purchaseCheckStartDate: null,
                purchaseCheckEndDate: null,
                isPurchaseCheckUsingDateRange: false,
              };

          return (
            <PurchaseCheck
              key={`${targetAudience.audience}-${enablePurchaseCheck}`}
              onChange={purchaseCheck => {
                setPurchaseCheckAll(purchaseCheck);
              }}
              value={purchaseCheckValue}
              disabled={state?.readOnly}
            />
          );
        })}
      </div>
    </>
  );
};
